import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { useAppSelector } from 'Store/index';
import { useIntl } from 'react-intl';
import colors from '../../../../../../config/theme/colors';
import { getAtcCodesByPlatform } from 'Routes/Medication/Document/config';
import ItemList from 'Components/ItemList';
import SearchDialog from 'Components/SearchDialog';
import { exists } from 'neuro-utils';
import Unit from 'Components/Unit';
import ActionButton from 'Components/ActionButton';
import { ClearIconStyled } from '..';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import type { TMedicationType } from '../../definitions/common';
import { actions as sessionActions } from 'Store/session';
import { useDispatch } from 'react-redux';

type TFieldName =
  | 'medication'
  | 'specificMedication'
  | 'specificAtc'
  | 'status'
  | 'minDuration'
  | 'maxDuration'
  | 'monthsSinceEnd';

const changeMedication =
  (onChange: (e: TOnChangeValues) => void, medications: 'noCropping' | Array<string>, name: string) =>
  (values: TOnChangeValues): void => {
    const valuesName = Object.keys(values)[0];
    if (!valuesName) return;
    const value = values[valuesName];
    if (valuesName === 'medicationName') {
      if (medications === 'noCropping') onChange({ [name]: [value as string] });
      if (Array.isArray(medications) && !medications.includes(value ? `${value}` : '')) {
        medications.push(value as string);
        onChange({ [name]: medications });
      }
    }
  };

const removeMedication = (
  onChange: (values: TOnChangeValues) => void,
  medications: 'noCropping' | string[],
  index: number,
  name: string,
): void => {
  if (Array.isArray(medications) && medications.length > 1) {
    const newMeds = medications.slice(0, index).concat(medications.slice(index + 1));
    onChange({ [name]: newMeds });
  } else if (Array.isArray(medications) && medications.length === 1) {
    onChange({ [name]: 'noCropping' });
  }
};

export const MedicationField = ({ platform, searchFields, onChange }: IOwnProps) => {
  const { locale } = useIntl();
  const medicationSettings = useAppSelector((s: IState) => s.settings.orgSettings.medicationSettings);

  const atcOpts = medicationSettings?.sleepApnea.localizations?.[locale as 'fi' | 'en'];

  const onChangeMedicationCriteria = (i: number) => (values: TOnChangeValues) => {
    const fieldName = Object.keys(values)[0] as TFieldName;
    const fieldValue: TFieldValue = values[fieldName];

    // Clone or initialize medication criteria
    let newMedicationCriteria: TMedicationType[] | 'noCropping' = (
      Array.isArray(searchFields.medication) && searchFields.medication.length > 0
        ? structuredClone(searchFields.medication)
        : [{ medication: null }]
    ) as TMedicationType[];

    const current = newMedicationCriteria[i] ?? {};

    // Handle duration fields
    if (fieldName === 'minDuration' || fieldName === 'maxDuration') {
      const currentDuration = current.durationMonths;
      const updatedDuration = {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        ...(typeof currentDuration === 'object' && currentDuration !== null ? currentDuration : {}),
        [fieldName]: fieldValue,
      } as Exclude<typeof currentDuration, 'noCropping'>;

      current.durationMonths = updatedDuration;
    } else {
      (current as Record<TFieldName, TFieldValue>)[fieldName] = fieldValue;
    }

    // Apply normalization logic
    if (
      (fieldName === 'specificMedication' || fieldName === 'specificAtc') &&
      (!Array.isArray(fieldValue) || fieldValue.length === 0)
    ) {
      current[fieldName] = 'noCropping';
    }

    if (fieldName === 'medication' && !current.status) {
      current.status = 'noCropping';
    }

    if (fieldName === 'status' && fieldValue !== 'earlierInUse') {
      current.monthsSinceEnd = 'noCropping';
    }

    const duration = current.durationMonths;
    if (duration && typeof duration === 'object' && !exists(duration.minDuration) && !exists(duration.maxDuration)) {
      current.durationMonths = 'noCropping';
    }

    // Reorder keys for consistent display
    const orderedFields: Record<string, unknown> = {};
    const displayOrder = [
      'medication',
      'specificMedication',
      'specificAtc',
      'status',
      'durationMonths',
      'monthsSinceEnd',
    ];

    for (const key of displayOrder) {
      const k = key as keyof typeof current;
      if (exists(current[k])) orderedFields[key] = current[k];
    }

    newMedicationCriteria[i] = orderedFields as TMedicationType;

    if (
      newMedicationCriteria.length === 1 &&
      typeof newMedicationCriteria[0] === 'object' &&
      !('medication' in newMedicationCriteria[0])
    ) {
      newMedicationCriteria = 'noCropping';
    }

    onChange({ medication: newMedicationCriteria });
  };

  const dispatch = useDispatch();
  const medicationFetch = (searchString: string) =>
    sessionActions.medicationSearch(dispatch, searchString) as unknown as Promise<Record<string, string>[]>;

  const medicationSearchStatus = useAppSelector(s => s.session.medicationSearchStatus);

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  return (
    <React.Fragment>
      {(Array.isArray(searchFields.medication) ? searchFields.medication : [{ medication: null }]).map((m, i, arr) => (
        <React.Fragment key={`searchCriteria${i}`}>
          <div
            style={{
              backgroundColor: colors.lightestGray,
              padding: '1rem',
              marginBottom: i === arr.length - 1 ? '1rem' : undefined,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ marginBottom: '1rem', color: colors.tertiaryText }}>
                {fm('extendedSearch.searchCriteriaTitle')}
                {` ${i + 1}`}
              </div>
              {arr.length > 1 && (
                <ClearIconStyled
                  onClick={() =>
                    Array.isArray(searchFields.medication) &&
                    onChange({ medication: searchFields.medication.filter((_, index) => index !== i) })
                  }
                />
              )}
            </div>
            <InputHandler
              type="Radio"
              editing={true}
              name="medication"
              formData={{
                onChange: (values: TOnChangeValues) => onChangeMedicationCriteria(i)(values),
                document: { medication: m.medication },
              }}
              options={[
                ...(platform === 'ms'
                  ? ['anyMedication', 'noMedication']
                  : platform === 'sleepApnea'
                    ? ['specificAtc'] /** Specific ATC only supports sleepApnea, see atcOpts const */
                    : []),
                'specificMedication',
              ]}
              optionFormatter={(name: string | number): string | React.JSX.Element => fm(`extendedSearch.opts.${name}`)}
            />
            {m.medication === 'specificAtc' && (
              <React.Fragment>
                <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, marginBottom: '1rem' }}>
                  {fm('extendedSearch.specificAtc')}
                </div>
                <InputHandler
                  type="Checkbox"
                  editing={true}
                  name="specificAtc"
                  formData={{
                    onChange: onChangeMedicationCriteria(i),
                    document: {
                      specificAtc: m.specificAtc,
                    },
                  }}
                  options={getAtcCodesByPlatform(medicationSettings, 'sleepApnea').filter(
                    c =>
                      c !== 'C01CA' &&
                      (!Array.isArray(searchFields.diagnosis) || !searchFields.diagnosis.includes('G47.3')
                        ? c !== 'N06BA14'
                        : true),
                  )}
                  optionFormatter={(name: string | number): string | React.JSX.Element => (
                    <React.Fragment>{`${name}${atcOpts ? ` - ${atcOpts[name]}` : ''}`}</React.Fragment>
                  )}
                />
              </React.Fragment>
            )}
            {m.medication === 'specificMedication' && (
              <React.Fragment>
                <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, marginBottom: '1rem' }}>
                  {fm('extendedSearch.specificMedication')}
                </div>
                <ItemList
                  items={
                    Array.isArray(m.specificMedication)
                      ? m.specificMedication.map((med: string, index: number, array: string[]) => {
                          return {
                            firstItem: med,
                            itemOnClick: (): void =>
                              removeMedication(
                                onChangeMedicationCriteria(i),
                                m.specificMedication ?? 'noCropping',
                                index,
                                'specificMedication',
                              ),
                            style: {
                              fontWeight: 600,
                              backgroundColor: colors.lightGray,
                              paddingLeft: '0.8rem',
                              alignItems: 'center',
                              marginBottom: index === array.length - 1 ? '1rem' : undefined,
                            },
                          };
                        })
                      : []
                  }
                  separator={{
                    content: fm('general.or'),
                    style: { padding: '0.5rem 0 0.5rem 0.8rem', textTransform: 'uppercase' },
                  }}
                  button={
                    <SearchDialog
                      formData={{
                        onChange: changeMedication(
                          onChangeMedicationCriteria(i),
                          m.specificMedication ?? 'noCropping',
                          'specificMedication',
                        ),
                        document: m.specificMedication,
                      }}
                      searchFn={medicationFetch}
                      resultToDoc={{
                        name: 'medicationName',
                        substanceString: 'medicationSubstances',
                        dosageForm: 'dosageForm',
                      }}
                      title={'medication.chooseMedication'}
                      info={'medication.medDialogInfoText'}
                      openCloseButton={{ width: 12, height: 3, fontSize: 16, text: 'general.new' }}
                      loading={medicationSearchStatus === 'loading'}
                      errorMsg={
                        medicationSearchStatus === 'failed' ? fm('general.medicationSearchFailedMessage') : null
                      }
                    />
                  }
                />
              </React.Fragment>
            )}
            {m.medication && (
              <React.Fragment>
                <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 1rem 0' }}>
                  {fm('extendedSearch.status')}
                </div>
                <InputHandler
                  type="Radio"
                  editing={true}
                  name="status"
                  formData={{
                    onChange: onChangeMedicationCriteria(i),
                    document: { status: m.status },
                  }}
                  options={['noCropping', 'inUse', 'earlierInUse'].concat(
                    m.medication === 'specificMedication' ? ['notInUse'] : [],
                  )}
                  optionFormatter={(name: string | number): string | React.JSX.Element =>
                    fm(`extendedSearch.opts.${name}`)
                  }
                />
              </React.Fragment>
            )}
            {platform === 'mgravis' && 'status' in m && (m.status === 'inUse' || m.status === 'earlierInUse') && (
              <React.Fragment>
                {(['durationMonths', 'monthsSinceEnd'] as ('durationMonths' | 'monthsSinceEnd')[])
                  .filter(f => m.status === 'earlierInUse' || f !== 'monthsSinceEnd')
                  .map((f, ii) => (
                    <React.Fragment key={`${f}.${ii}`}>
                      <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 1rem 0' }}>
                        {fm(`extendedSearch.${f}`)}
                      </div>
                      <Unit unit={fm('extendedSearch.monthsUnit')}>
                        {f === 'durationMonths' ? (
                          <React.Fragment>
                            {(['minDuration', 'maxDuration'] as ('minDuration' | 'maxDuration')[]).map((n, jj) => (
                              <React.Fragment key={jj}>
                                <InputHandler
                                  type="NumberField"
                                  editing={true}
                                  name={n}
                                  formData={{
                                    onChange: onChangeMedicationCriteria(i),
                                    document: {
                                      [n]: m[f] && m[f] !== 'noCropping' ? m[f][n] : undefined,
                                    },
                                  }}
                                  min={0}
                                  placeholder={`extendedSearch.${{ 0: 'min', 1: 'max' }[jj]}`}
                                  width={9}
                                />
                                {jj === 0 && ' - '}
                              </React.Fragment>
                            ))}
                          </React.Fragment>
                        ) : (
                          <InputHandler
                            type="NumberField"
                            editing={true}
                            name={f}
                            formData={{
                              onChange: onChangeMedicationCriteria(i),
                              document: { [f]: exists(m[f]) && m[f] !== 'noCropping' ? m[f] : undefined },
                            }}
                            min={0}
                            placeholder={'extendedSearch.min'}
                            width={9}
                          />
                        )}
                      </Unit>
                    </React.Fragment>
                  ))}
              </React.Fragment>
            )}
          </div>
          {arr.length > 1 && i < arr.length - 1 && (
            <div style={{ padding: '1rem', textTransform: 'uppercase' }}>{fm('general.and')}</div>
          )}
        </React.Fragment>
      ))}
      <div style={{ marginTop: '2rem', justifyContent: 'center' }}>
        <ActionButton
          text={'extendedSearch.addSearchCriteria'}
          onClick={() => {
            const newMedication = structuredClone(
              Array.isArray(searchFields.medication) && searchFields.medication.length > 0
                ? searchFields.medication
                : [{ medication: null }],
            ) as (TMedicationType | { medication: null })[];
            newMedication.push({ medication: null });
            onChange({ medication: newMedication });
          }}
          width={16}
          height={3}
          fontSize={16}
        />
      </div>
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'platform' | 'searchFields' | 'onChange'> {}

export default MedicationField;
